import { WSFilter } from "@wingspanhq/fe-component-library";
import { PayeeReviewStatus } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { usePayeeFilterOptions } from "../../../../Payees/hooks/usePayeeFilterOptions";
import { PAYABLES_STATUS_OPTIONS } from "./statusOptions";
import { PayablesFilterKey, PayablesVisibleFilters } from "./types";
import { useBulkBatchFilterOptions } from "./useBulkBatchFilterOptions";

export function useBuildConfig(
  visibleFilters?: PayablesVisibleFilters
): WSFilter[] {
  const payeeOptions = usePayeeFilterOptions();
  const bulkBatchOptions = useBulkBatchFilterOptions();

  return useMemo((): WSFilter[] => {
    const config: WSFilter[] = [];

    // Single-select: each option is a compound of `status` and
    // `payerApprovalStatus`, which cannot be OR'd together in one request.
    config.push({
      title: "Status",
      name: "status",
      type: "radio",
      titleInLabel: true,
      options: PAYABLES_STATUS_OPTIONS
    });

    config.push({
      title: "Contractor",
      name: "payeeId",
      type: "select",
      apiOptions: payeeOptions
    });

    config.push({
      title: "Contractor review status",
      name: "payeeReviewStatus",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "None",
          helperText: "Contractor has not reviewed the payable yet",
          name: PayeeReviewStatus.PENDING
        },
        {
          label: "Accepted",
          name: PayeeReviewStatus.ACCEPTED
        },
        {
          label: "Disputed",
          name: PayeeReviewStatus.DISPUTED
        },
        {
          label: "Resubmitted",
          name: PayeeReviewStatus.RESUBMITTED
        }
      ]
    });

    config.push({
      title: "Bulk batch",
      name: "bulkBatchId",
      type: "select",
      searchable: true,
      options: bulkBatchOptions
    });

    config.push({
      title: "Due date",
      name: "dueDateRange",
      type: "daterange",
      titleInLabel: true,
      options: ["all", "pastWeek", "pastMonth", "yearToDate", "custom"]
    });

    config.push({
      title: "Paid date",
      name: "paidDateRange",
      type: "daterange",
      titleInLabel: true,
      options: ["all", "pastWeek", "pastMonth", "yearToDate", "custom"]
    });

    config.push({
      title: "Opened date",
      name: "openedDateRange",
      type: "daterange",
      titleInLabel: true,
      options: ["all", "pastWeek", "pastMonth", "yearToDate", "custom"]
    });

    if (!visibleFilters) {
      return config;
    }

    return config.filter(
      f => visibleFilters[f.name as PayablesFilterKey] === true
    );
  }, [payeeOptions, bulkBatchOptions, visibleFilters]);
}
